// PLZ (German postal code) coverage checker. "Type your ZIP — we check instantly."
import { useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, CheckCircle2, XCircle, ArrowRight, Loader2, Zap } from "lucide-react";
import axios from "axios";
import { API } from "../lib/api";
import { useLang } from "../contexts/LangContext";

// Plain axios call (no credentials, no shared interceptors) — coverage is a
// public read-only lookup. Keeping it credential-less avoids preflight failures
// and intermittent 502s caused by Cloudflare/edge proxies on the production
// domain. We also add an explicit short timeout and 2 silent retries with
// exponential backoff so a transient edge hiccup never surfaces to the user.
async function fetchCoverage(plz) {
  const url = `${API}/coverage/${plz}`;
  const delays = [0, 400, 1200]; // 3 attempts
  let lastErr;
  for (let i = 0; i < delays.length; i++) {
    if (delays[i] > 0) {
      await new Promise((r) => setTimeout(r, delays[i]));
    }
    try {
      return await axios.get(url, {
        timeout: 8000,
        withCredentials: false,
        headers: { Accept: "application/json" },
      });
    } catch (err) {
      lastErr = err;
      // 4xx (e.g. invalid PLZ) → don't retry, surface immediately
      const status = err?.response?.status;
      if (status && status >= 400 && status < 500) break;
    }
  }
  throw lastErr;
}

export default function PLZChecker() {
  const { t } = useLang();
  const ct = t.plz_checker;

  const [plz, setPlz] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e?.preventDefault();
    setError("");
    setResult(null);
    if (!/^\d{5}$/.test(plz)) {
      setError(ct.error_format);
      return;
    }
    setLoading(true);
    try {
      const r = await fetchCoverage(plz);
      setResult(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail || ct.error_generic);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-16 lg:py-24 bg-neutral-50 dark:bg-[#0A0B0D]" data-testid="plz-checker-section">
      <div className="max-w-4xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#FF5A00] mb-3">
            {ct.eyebrow}
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter mb-4">
            {ct.title}
          </h2>
          <p className="text-sm sm:text-base text-neutral-600 dark:text-neutral-400 max-w-2xl mx-auto leading-relaxed">
            {ct.subtitle}
          </p>
        </div>

        <form
          onSubmit={submit}
          className="bg-white dark:bg-[#0F1012] border border-neutral-200 dark:border-neutral-800 p-6 sm:p-8 shadow-xl"
        >
          <label className="block">
            <span className="block text-[11px] font-bold tracking-[0.22em] uppercase text-neutral-500 mb-3">
              {ct.label}
            </span>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <MapPin
                  size={18}
                  className="absolute start-3.5 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none"
                />
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{5}"
                  maxLength={5}
                  value={plz}
                  onChange={(e) => setPlz(e.target.value.replace(/\D/g, "").slice(0, 5))}
                  placeholder={ct.placeholder}
                  className="w-full ps-11 pe-4 py-3.5 text-lg font-bold tracking-widest border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 focus:outline-none focus:border-[#FF5A00] tabular-nums"
                  data-testid="plz-checker-input"
                  autoComplete="postal-code"
                />
              </div>
              <button
                type="submit"
                disabled={loading || plz.length !== 5}
                className="bg-[#FF5A00] hover:bg-[#E04F00] text-white font-bold tracking-wider uppercase text-sm px-6 py-3.5 disabled:opacity-60 disabled:cursor-not-allowed transition-colors inline-flex items-center justify-center gap-2"
                data-testid="plz-checker-submit"
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> {ct.checking}
                  </>
                ) : (
                  <>
                    {ct.check_btn}
                    <ArrowRight size={14} />
                  </>
                )}
              </button>
            </div>
          </label>

          {error && (
            <div className="mt-5 text-sm text-rose-600 bg-rose-50 dark:bg-rose-900/20 px-4 py-3 border border-rose-200 dark:border-rose-900/40">
              {error}
            </div>
          )}

          {result && result.covered && (
            <div
              className="mt-6 p-5 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 animate-[slideUp_.4s_cubic-bezier(.16,1,.3,1)]"
              data-testid="plz-checker-result-yes"
            >
              <div className="flex items-start gap-3 mb-4">
                <CheckCircle2 size={22} className="text-emerald-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="text-emerald-900 dark:text-emerald-200 font-black text-base">
                    {ct.covered_yes.replace("{city}", result.city)}
                  </div>
                  <div className="text-emerald-700 dark:text-emerald-300 text-sm mt-1">
                    {ct.distance.replace("{km}", result.distance_km).replace("{min}", result.estimated_min)}
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-3 items-center pt-3 border-t border-emerald-200 dark:border-emerald-800">
                {result.express_available && (
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-bold tracking-wider uppercase bg-[#FF5A00] text-white px-3 py-1.5">
                    <Zap size={11} /> {ct.express}
                  </span>
                )}
                <Link
                  to="/book"
                  className="ms-auto bg-[#090A0B] hover:bg-[#FF5A00] text-white font-bold tracking-wider uppercase text-xs px-5 py-2.5 transition-colors inline-flex items-center gap-2"
                  data-testid="plz-checker-book-now"
                >
                  {ct.book_now}
                  <ArrowRight size={12} />
                </Link>
              </div>
            </div>
          )}

          {result && !result.covered && (
            <div
              className="mt-6 p-5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 animate-[slideUp_.4s_cubic-bezier(.16,1,.3,1)]"
              data-testid="plz-checker-result-no"
            >
              <div className="flex items-start gap-3">
                <XCircle size={22} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="text-amber-900 dark:text-amber-200 font-bold">
                    {ct.covered_no_title}
                  </div>
                  <p className="text-amber-700 dark:text-amber-300 text-sm mt-1 leading-relaxed">
                    {ct.covered_no_body}
                  </p>
                  <Link
                    to="/#contact"
                    className="inline-flex items-center gap-1.5 mt-3 text-[#FF5A00] font-bold text-sm hover:underline"
                  >
                    {ct.contact_us} <ArrowRight size={12} />
                  </Link>
                </div>
              </div>
            </div>
          )}
        </form>
      </div>
    </section>
  );
}
